import { Controller } from '@hotwired/stimulus';

// The daisyUI theme switch, one copy for every host (fire/docs/HOTWIRE.md).
//
// The pair of themes is declared once, on the page, not on the switch:
//
//   <html data-themes='["umcanto", "umcanto-dark"]'>   <%# light first %>
//
// and a dark app adds `data-theme-default="dark"` beside it, which is the
// only thing that differs between qir and the rest.
//
// so a toggle is only ever this, anywhere on the page, however many of them:
//
//   <button data-controller="theme" data-action="theme#toggle">
//
// Nothing stored means "whatever the system says" — daisyUI's default and
// prefersdark pair — and a click pins the other one. `fire_theme_script`
// (ThemeHelper) puts a pinned theme back in <head> before the first paint,
// so the page never opens in one theme and snaps to the other a frame later.
//
// localStorage, not a cookie: the theme is the browser's business, never the
// server's, and it costs nothing on every request. Private windows and
// blocked site data throw on access rather than returning null, so every
// touch is guarded — a page that cannot remember the theme still switches it.
//
// No relative imports on purpose: hosts on importmap serve this file straight
// off the asset path, where `../lib/x` has no root to resolve against.
export default class extends Controller {
  static targets = ['checkbox'];
  static values = { key: { type: String, default: 'theme' } };

  // A daisyUI `swap` switch is a checkbox, and its :checked is what flips the
  // sun for the moon. The server cannot render that state any more — the
  // theme lives in the browser now — so it gets set here on every page load.
  connect() {
    this.sync();
  }

  // Next one along, wrapping. Two themes make it the toggle everyone wants;
  // more than two make it a cycle.
  toggle() {
    const themes = this.themes;
    if (!themes.length) return;

    const next = themes[(themes.indexOf(this.current) + 1) % themes.length];
    document.documentElement.dataset.theme = next;
    this.store(next);
    this.sync();
    this.dispatch('change', { detail: { theme: next } });
  }

  // Checked means the dark one — second in the pair, by the light-first
  // convention that `data-themes` and the head script both follow.
  // The controller goes on the switch itself, never on <html>: this reaches
  // for the checkbox inside it, and document-wide that is whatever box the
  // page happens to render first (a drawer toggle, usually).
  sync() {
    const box = this.hasCheckboxTarget
      ? this.checkboxTarget
      : this.element.querySelector('input[type="checkbox"]');
    if (box) box.checked = this.current === this.themes[1];
  }

  // What is on screen right now — the pinned one, else what the system asked
  // for. Reading the attribute first keeps this honest if something else set
  // it, and `fire_theme_script` always has, so the fallback below only runs on
  // a host that forgot the head script. It mirrors that script exactly (same
  // `data-theme-default`), or the first click on such a host goes backwards.
  get current() {
    const themes = this.themes;
    const shown = document.documentElement.dataset.theme;
    if (themes.includes(shown)) return shown;

    const d = document.documentElement.dataset.themeDefault === 'dark' ? 1 : 0;
    const asked = matchMedia(`(prefers-color-scheme: ${d ? 'light' : 'dark'})`).matches;
    return themes[asked ? 1 - d : d];
  }

  get themes() {
    try {
      return JSON.parse(document.documentElement.dataset.themes || '[]');
    } catch {
      return [];
    }
  }

  store(name) {
    try {
      localStorage[this.keyValue] = name;
    } catch {
      // Private window, or site data blocked. Switching still works, it just
      // will not be remembered — which is the browser's call, not a failure.
    }
  }
}
